<script setup lang="ts" generic="ModelValueType extends string | number">
//#region import
import { computed } from 'vue';
import _ from '@/setup/lodash';
import type { ElRadioGroup } from 'element-plus';
import type { ListType, FilterOp } from '@/types/core';
//#endregion

//#region types
export interface OperatorListItem {
  value: FilterOp,
  name: string,
}

export interface Props {
  list?: string[] | ListType[],
  label?: string,
  size?: 'big' | 'small' | 'default',
  disabled?: boolean,
  width?: string,
}

export interface Emits<T> {
  'change': [value: T],
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  list: () => [],
  label: '',
  size: 'default',
  disabled: false,
  width: '',
});
const emit = defineEmits<Emits<ModelValueType>>();
const modelValue = defineModel<ModelValueType>();
//#endregion

//#region computed
const pList = computed(() => {
  if (isFlat.value) {
    return props.list.map((item) => ({ value: item, name: item } as ListType));
  }
  return props.list as ListType[];
});
const isFlat = computed(() => {
  return _.some(props.list, (item) => !_.isObject(item));
});
//#endregion

//#region methods
const handleChange = async (value: ModelValueType) => {
  emit('change', value);
};
//#endregion
</script>

<template>
  <div
    :class="[
      'ui-tabs',
      `ui-tabs_${size}`
    ]"
  >
    <UiLabel
      v-if="label"
      :label="label"
      class="t:mb-8"
    />
    <ElRadioGroup
      v-model="modelValue"
      :disabled="disabled"
      :style="{
        width: width !== '' ? `${width}px` : '100%',
      }"
      @change="handleChange($event as ModelValueType)"
    >
      <ElRadioButton
        v-for="(item, id) in pList"
        :key="id + item.value"
        :value="item.value"
        :disabled="item.disabled"
      >
        <template v-slot>
          <div class="ui-tabs__item">
            {{ item.name }}
          </div>
        </template>
      </ElRadioButton>
    </ElRadioGroup>
  </div>
</template>

<style lang="scss">
.ui-tabs {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
  .el-radio-group {
    padding: 1px;
    flex-wrap: nowrap;
  }
  .el-radio-button{
    //outline: none !important;
    //box-shadow: none;
    flex: 1;
    &__inner{
      width: 100%;
      box-shadow: none;
      outline: 1px solid var(--border-default);
      padding: 0;
      font-weight: 400;
      text-transform: capitalize;
      color: var(--text-body);
    }
    .el-radio-button__original-radio:not(:disabled)+.el-radio-button__inner:hover .ui-tabs__item{
      background-color: var(--hover-theme-color);
    }
    &.is-active .el-radio-button__original-radio{
      &:not(:disabled),
      &:disabled{
        &+.el-radio-button__inner{
          box-shadow:none;
          background-color: var(--background-primary);
          color: var(--text-body-invert);
          border-color: var(--background-primary);
        }
      }
    }
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
  }

  &_big &{
    &__item {
      padding: 0 16px;
      height: 32px;
    }
  }
  &_default &{
    &__item {
      padding: 0 12px;
      height: 28px;
    }
  }
  &_small &{
    &__item {
      padding: 0 12px;
      height: 24px;
    }
  }
}
</style>

